'use client'
import { useState } from 'react'
import { ClipboardList, PenLine, ChevronDown, ChevronUp, Loader2, Clock, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { useAppendDiagnosisSummary } from '@/hooks/usePatients'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import type { Patient } from '@/types'

interface HistoryEntry {
  text: string
  date: string
  user_id: string
  author_name: string
}

interface DiagnosisSummaryCardProps {
  patientId: string
  history: HistoryEntry[]
  /** Called with the updated patient after a new entry is appended, so callers
   *  holding patient state (e.g. the active-patient context) can refresh. */
  onAppended?: (patient: Patient) => void
}

function AuthorInitials({ name }: { name: string }) {
  const parts = name.trim().split(/\s+/)
  const initials = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase()
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ml-primary/10 text-[11px] font-bold text-ml-primary">
      {initials}
    </span>
  )
}

export function DiagnosisSummaryCard({ patientId, history, onAppended }: DiagnosisSummaryCardProps) {
  const { formatDateTime } = useClinicFormat()
  const [appendOpen, setAppendOpen] = useState(false)
  const [text, setText] = useState('')
  const [showHistory, setShowHistory] = useState(false)

  const { toast } = useToast()
  const { mutate: appendSummary, isPending } = useAppendDiagnosisSummary()

  const latest = history.length > 0 ? history[history.length - 1] : null
  const previous = history.slice(0, -1)

  function handleAppend() {
    if (!text.trim()) return
    appendSummary(
      { patientId, text: text.trim() },
      {
        onSuccess: (updated) => {
          setText('')
          setAppendOpen(false)
          onAppended?.(updated)
          toast({ title: 'Resumen actualizado', description: 'La entrada fue agregada al historial.' })
        },
        onError: () => {
          toast({ title: 'Error', description: 'No se pudo guardar el resumen.', variant: 'destructive' })
        },
      }
    )
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-ml-outline-variant/15 bg-ml-surface shadow-sm">
      {/* Top accent bar */}
      <div className="h-1 w-full bg-gradient-to-r from-ml-primary/60 via-ml-primary/30 to-transparent" aria-hidden />
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-ml-outline-variant/10 bg-ml-surface-container-low/60 px-5 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ml-primary/10">
            <ClipboardList className="h-4 w-4 text-ml-primary" />
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-ml-primary">
              Resumen Diagnóstico
            </h3>
            {latest ? (
              <p className="text-[10px] text-ml-on-surface-variant/60">
                Actualizado por {latest.author_name} · {formatDateTime(latest.date)}
              </p>
            ) : (
              <p className="text-[10px] text-ml-on-surface-variant/50">Sin entradas</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {previous.length > 0 && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-ml-on-surface-variant transition-colors hover:bg-ml-surface-container-high hover:text-ml-primary"
            >
              {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {previous.length} anterior{previous.length > 1 ? 'es' : ''}
            </button>
          )}
          <Dialog open={appendOpen} onOpenChange={setAppendOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-[11px] font-medium text-ml-on-surface-variant hover:bg-ml-primary/10 hover:text-ml-primary"
                title="Agregar entrada"
              >
                <Plus className="h-3.5 w-3.5" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-ml-primary" />
                  Nueva entrada diagnóstica
                </DialogTitle>
                <DialogDescription>
                  Agrega una nota al historial diagnóstico del paciente. Las entradas anteriores se conservan y no pueden modificarse.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Escribe el resumen de diagnóstico..."
                className="min-h-[140px] resize-none font-sans text-sm"
                disabled={isPending}
                autoFocus
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setAppendOpen(false)} disabled={isPending}>
                  Cancelar
                </Button>
                <Button onClick={handleAppend} disabled={isPending || !text.trim()}>
                  {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PenLine className="mr-2 h-4 w-4" />}
                  Guardar entrada
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Latest entry — highlighted "current" state */}
      <div className="px-5 py-4">
        {latest ? (
          <div className="relative rounded-xl border border-ml-primary/15 bg-ml-primary/5 px-4 py-3.5">
            <span className="absolute right-3 top-3 rounded bg-ml-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-ml-primary">
              Actual
            </span>
            <div className="mb-2.5 flex items-center gap-2">
              <AuthorInitials name={latest.author_name} />
              <div>
                <p className="text-xs font-semibold text-ml-on-surface">{latest.author_name}</p>
                <p className="flex items-center gap-1 text-[10px] text-ml-on-surface-variant/60">
                  <Clock className="h-2.5 w-2.5" />
                  {formatDateTime(latest.date)}
                </p>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-ml-on-surface whitespace-pre-wrap">
              {latest.text}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded bg-ml-surface-container-high">
              <ClipboardList className="h-5 w-5 text-ml-on-surface-variant/40" />
            </div>
            <div>
              <p className="text-sm font-medium text-ml-on-surface-variant/70">
                Sin resumen diagnóstico
              </p>
              <p className="mt-0.5 text-xs text-ml-on-surface-variant/50">
                Usa el botón «Agregar» para registrar la primera impresión clínica.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* History timeline */}
      {showHistory && previous.length > 0 && (
        <div className="border-t border-ml-outline-variant/10 bg-ml-surface-container-low/40 px-5 py-4">
          <p className="mb-3 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-ml-on-surface-variant/60">
            <Clock className="h-3 w-3" />
            Entradas anteriores
          </p>
          <ul className="relative space-y-0 pl-4">
            {/* Vertical connecting line */}
            <div className="absolute left-[7px] top-3 bottom-3 w-px bg-ml-outline-variant/20" aria-hidden />
            {[...previous].reverse().map((entry, i) => (
              <li key={i} className="relative pb-5 last:pb-0">
                {/* Timeline dot */}
                <div className="absolute -left-[13px] top-[5px] h-3 w-3 rounded-full border-2 border-ml-outline-variant/30 bg-ml-surface" aria-hidden />
                <div className="rounded-xl border border-ml-outline-variant/10 bg-ml-surface px-4 py-3 shadow-sm">
                  <div className="mb-2 flex items-center gap-2">
                    <AuthorInitials name={entry.author_name} />
                    <div>
                      <p className="text-xs font-semibold text-ml-on-surface">{entry.author_name}</p>
                      <p className="flex items-center gap-1 text-[10px] text-ml-on-surface-variant/60">
                        <Clock className="h-2.5 w-2.5" />
                        {formatDateTime(entry.date)}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-ml-on-surface/80 whitespace-pre-wrap">
                    {entry.text}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
